import { ColumnFilterOrDisplayType, ConfigColumns2 } from "../../types/configColumns2";
import { momentDtoToLocalStringByCulture } from "../../aplUtils/dateHelper";
import { WorkorderBob } from "../../types/workorderBob";
import { Culture } from "../../types/culture";
import { WorkorderItem } from "../../types/workorderItem";
import React from "react";
import { renderToString } from "react-dom/server";

import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { Guid } from "guid-typescript";
import { translateString } from "../../translations/gridTranslationHelper";
import { getWorkorderStatus } from "../../aplUtils/statusConditionEnumHelper";
import { truncateString } from "../../aplUtils/stringUtills";

const generateBobTooltipSearchString = function (data: WorkorderBob[]): string {
    return data.map((x: WorkorderBob) => `${x.batch} ${x.donorWorkorder} ${x.materialSapCode}`).join("\n");
};

const generateBobTooltipJsx = function (data: WorkorderBob[], culture: Culture): JSX.Element {
    if (data.length === 0) return <React.Fragment></React.Fragment>;

    const tooltipId = Guid.create();
    return (
        <React.Fragment>
            <Tooltip id={`bob-tooltip-${tooltipId}`} html={renderToString(generateBobTooltipContent(data, culture))} />
            <span className="badge badge-primary ml-1" data-tooltip-delay-hide={666} data-tooltip-id={`bob-tooltip-${tooltipId}`}>
                {data.length}
            </span>
        </React.Fragment>
    );
};

const generateBobTooltipContent = function (data: WorkorderBob[], culture: Culture): JSX.Element {
    return (
        <React.Fragment>
            <span style={{ zIndex: 3000 }}>
                <div className="row">
                    <div className="col nowrap">
                        <b>{translateString("batch", culture)}</b>
                    </div>
                    <div className="col nowrap">
                        <b>{translateString("workOrder", culture)}</b>
                    </div>
                    <div className="col nowrap">
                        <b>{translateString("material", culture)}</b>
                    </div>
                </div>
                {data.map((x: WorkorderBob, i: number) => (
                    <div className="row">
                        <div className="col nowrap">
                            <b>{x.batch} </b>
                        </div>
                        <div className="col nowrap">
                            <b>{x.donorWorkorder} </b>
                        </div>
                        <div className="col nowrap">
                            <b>{x.materialSapCode} </b>
                        </div>
                    </div>
                ))}
            </span>
        </React.Fragment>
    );
};

const workorderManagementColumns = (siteId: number, culture: Culture): ConfigColumns2[] => {
    const workorderId = { data: "workorderId", visible: false, title: "workorderId" };
    const workorder = { data: "workorder", className: "nowrap", width: "12%", title: "workOrder" };
    const batch = {
        data: "batch",
        className: "nowrap",
        width: "12%",
        title: "batch",
        render: function (data: WorkorderItem, type: ColumnFilterOrDisplayType | undefined) {
            if ((data.workorderBatches ?? []).length === 0) return "";
            
            let trunc = truncateString(
                data.workorderBatches
                    .map(x => x.batch)
                    .sort()
                    .reverse()
                    .join(", "),
                24
            );

            if (type === "sort") return trunc.input;
            if (type === "filter") return trunc.input;
            if (type === "display") return trunc.truncated;
        }
    };
    const workorderBobs = {
        data: "workorderBobs",
        className: "nowrap",
        render: function (data: WorkorderItem, type: ColumnFilterOrDisplayType | undefined) {
            if (type === "sort") return data.workorderBobs.length.toString();
            if (type === "filter") return generateBobTooltipSearchString(data.workorderBobs);
            if (type === "display") return generateBobTooltipJsx(data.workorderBobs, culture);
        },
        width: "12%",
        title: "billOfBatches"
    };
    const materialSapCode = { data: "materialSapCode", width: "10%", title: "material" };
    const speakingPartNo = { data: "speakingPartNo", width: "12%", title: "material12" };
    const description = { data: "description", className: "nowrap", width: "24%", title: "description" };
    const startDate = {
        data: "startDate",
        className: "nowrap",
        render: function (data: WorkorderItem, type: ColumnFilterOrDisplayType | undefined) {
            return type === "sort" ? data.startDate : momentDtoToLocalStringByCulture(data.startDate, culture);
        },
        width: "10%",
        title: "scheduledDateTime"
    };
    const lineDescription = { data: "lineDescription", className: "nowrap", width: "5%", title: "workCenter", dropdown: true };

    const statusConsumption = {
        data: "statusConsumption",
        render: function (data: WorkorderItem, type: ColumnFilterOrDisplayType | undefined) {
            return getWorkorderStatus(data.statusConsumption, culture);
        },
        className: "nowrap",
        width: "8%",
        title: "consumptionStatus",
        dropdown: true
    };
    const statusLabelling = {
        data: "statusLabelling",
        render: function (data: WorkorderItem, type: ColumnFilterOrDisplayType | undefined) {
            return getWorkorderStatus(data.statusLabelling, culture);
        },
        className: "nowrap",
        width: "8%",
        title: "labellingStatus",
        dropdown: true
    };

    const inLine = {
        data: "inLine",
        render: function (data: WorkorderItem, type: ColumnFilterOrDisplayType | undefined) {
            return data.inLine ? translateString("rxg_Yes", culture)  : translateString("rxg_No", culture)
        },
        className: "nowrap",
        width: "5%",
        title: "inLine",
        dropdown: true
    }

    const inLineDescription = {
        data: "inLineDescription",
        render: function (data: WorkorderItem, type: ColumnFilterOrDisplayType | undefined) {
            return data.inLineDescription ? data.inLineDescription : ""
        },
        className: "nowrap",
        width: "5%",
        title: "lineIn",
        dropdown: true
    }

    let columndata: ConfigColumns2[] = [];
    if(siteId === 22){
        columndata.push(workorderId);
        columndata.push(workorder);
        columndata.push(batch);
        columndata.push(workorderBobs);
        columndata.push(materialSapCode);
        columndata.push(description);
        columndata.push(inLineDescription);
        columndata.push(inLine);
        columndata.push(startDate);
        columndata.push(lineDescription);
        columndata.push(statusConsumption);
        columndata.push(statusLabelling);
    }
    else if (siteId > 0 && siteId <= 4) {
        columndata.push(workorderId);
        columndata.push(workorder);
        columndata.push(batch);
        columndata.push(workorderBobs);
        columndata.push(materialSapCode);
        columndata.push(speakingPartNo);
        columndata.push(description);
        columndata.push(startDate);
        columndata.push(lineDescription);
        columndata.push(statusConsumption);
        columndata.push(statusLabelling);
    } else {
        columndata.push(workorderId);
        columndata.push(workorder);
        columndata.push(batch);
        columndata.push(workorderBobs);
        columndata.push(materialSapCode);
        columndata.push(description);
        columndata.push(startDate);
        columndata.push(lineDescription);
        columndata.push(statusConsumption);
        columndata.push(statusLabelling);
    }

    return columndata;
};

export { workorderManagementColumns };
